import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import SongDetailView from './components/SongDetailView';
import SongDetailEdit from './components/SongDetailEdit';
import { getSongById, updateSong } from '../../services/songService';
import { musicalService } from '../../services/musicalService';
import { userService } from '../../services/userService';
import characterSongAnalysisService from '../../services/characterSongAnalysisService';
import { useChat } from '../../hooks/useChat';
import toast from 'react-hot-toast';

// Character Selection Modal Component
const CharacterSelectionModal = ({ characters, onSelect, onClose, onCreateCharacter }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
          <h3 className="text-lg font-semibold text-white">
            Select Character to Edit
          </h3>
          <p className="text-sm text-blue-100 mt-1">
            Choose which character version of this song you want to edit
          </p>
        </div>
        
        <div className="p-6 max-h-96 overflow-y-auto">
          {characters?.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Characters Assigned</h4>
              <p className="text-gray-600 mb-6">
                This song needs to have characters assigned before you can add detailed analysis information (Overview and Levels).
              </p>
              <Button
                variant="primary"
                onClick={onCreateCharacter}
                iconName="Plus"
                iconPosition="left"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 rounded-lg shadow-lg transition-all duration-200"
              >
                Assign a Role First
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {characters?.map((character) => (
                <div
                  key={character?.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onSelect(character)}
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {character?.role?.character_name || 'Unknown Character'}
                    </h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      {character?.role?.voice_type && (
                        <p>Voice Type: {character?.role?.voice_type?.replace('_', ' ')}</p>
                      )}
                      {character?.role?.role_type && (
                        <p>Role Type: {character?.role?.role_type}</p>
                      )}
                      {character?.role?.musicals?.title && (
                        <p>Musical: {character?.role?.musicals?.title}</p>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add this block - Create local SongAnalysisService object
const SongAnalysisService = {
  getSongAnalysisById: async (songId) => {
    // This is a placeholder implementation
    // Replace with actual service call when available
    return { data: null };
  }
};

const SongDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user, isSuperUser } = useAuth();
  const [song, setSong] = useState(null);
  const [musicals, setMusicals] = useState([]);
  const [characterAnalyses, setCharacterAnalyses] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [showCharacterModal, setShowCharacterModal] = useState(false);

  // Featured & Favorite state
  const [isFeatured, setIsFeatured] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // AI generation state
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiSongContext, setAiSongContext] = useState('');
  const [aiEmotionalArc, setAiEmotionalArc] = useState('');
  const [aiPerformanceTips, setAiPerformanceTips] = useState('');
  const [aiGenerationDone, setAiGenerationDone] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const { response: aiResponse, isLoading: isAiLoading, error: aiError, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (aiError) toast?.error(aiError?.message || 'AI generation failed');
  }, [aiError]);

  useEffect(() => {
    if (aiResponse && !isAiLoading) {
      // Parse the structured JSON response
      try {
        const jsonMatch = aiResponse?.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch?.[0]);
          setAiSongContext(parsed?.song_context || '');
          setAiEmotionalArc(parsed?.emotional_arc || '');
          setAiPerformanceTips(parsed?.performance_tips || '');
        } else {
          // Fallback: put entire response in song context
          setAiSongContext(aiResponse);
        }
        setAiGenerationDone(true);
      } catch {
        setAiSongContext(aiResponse);
        setAiGenerationDone(true);
      }
    }
  }, [aiResponse, isAiLoading]);

  const handleGenerateAi = () => {
    const songTitle = song?.title;
    const musicalTitle = song?.musical?.title;
    const characterName = selectedCharacter?.role?.character_name || characterAnalyses?.[0]?.role?.character_name;

    if (!songTitle) {
      toast?.error('Song title is required for AI generation');
      return;
    }

    setAiGenerationDone(false);
    setAiSongContext('');
    setAiEmotionalArc('');
    setAiPerformanceTips('');

    sendMessage([
      {
        role: 'system',
        content: `You are a musical theater expert and vocal coach. Given a song title, musical, and character, generate three distinct sections of analysis. Always respond with valid JSON only, no markdown or extra text.`
      },
      {
        role: 'user',
        content: `Generate analysis for the song "${songTitle}"${musicalTitle ? ` from the musical "${musicalTitle}"` : ''}${characterName ? ` sung by the character "${characterName}"` : ''}.

Return a JSON object with exactly these three keys:
{
  "song_context": "2-3 sentences about the song's dramatic context, where it occurs in the story, and what the character is experiencing at this moment.",
  "emotional_arc": "2-3 sentences describing the emotional journey of the song from beginning to end — what the character feels at the start, how it shifts, and where they land emotionally.",
  "performance_tips": "3-4 practical performance tips for an actor/singer performing this song, covering vocal approach, physicality, and character intention."
}`
      }
    ], { max_completion_tokens: 600 });
  };

  const handleCopyField = (fieldName, value) => {
    navigator.clipboard?.writeText(value);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Get song ID from URL parameters (both path and query params)
  const getIdFromUrl = () => {
    // First try to get from path parameters (like /song-detail/123)
    if (params?.id) {
      return params?.id;
    }
    
    // Fallback to query parameters (like /song-detail-page?id=123)
    const searchParams = new URLSearchParams(location?.search);
    return searchParams?.get('id');
  };

  const songId = getIdFromUrl();

  useEffect(() => {
    loadSongData();
    loadMusicals();
    loadCharacterAnalyses();
  }, [songId]);

  useEffect(() => {
    if (song) {
      setIsFeatured(!!song?.is_featured);
    }
  }, [song]);

  useEffect(() => {
    if (user && songId) {
      userService?.isFavoriteSong(user?.id, songId)?.then(({ isFavorite: fav }) => {
        setIsFavorite(!!fav);
      });
    }
  }, [user, songId]);

  const handleToggleFeatured = async () => {
    if (!isSuperUser()) return;
    const newValue = !isFeatured;
    setIsFeatured(newValue);
    setFeaturedLoading(true);
    try {
      const { error: updateError } = await updateSong(songId, { is_featured: newValue });
      if (updateError) {
        setIsFeatured(!newValue);
        toast?.error('Failed to update featured status');
      } else {
        toast?.success(newValue ? 'Song marked as featured' : 'Song removed from featured');
        setSong(prev => ({ ...prev, is_featured: newValue }));
      }
    } catch {
      setIsFeatured(!newValue);
      toast?.error('Failed to update featured status');
    } finally {
      setFeaturedLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user) return;
    const newValue = !isFavorite;
    setIsFavorite(newValue);
    setFavoriteLoading(true);
    try {
      if (newValue) {
        const { error: addError } = await userService?.addFavoriteSong(user?.id, songId);
        if (addError) {
          setIsFavorite(!newValue);
          toast?.error('Failed to add to favorites');
        } else {
          toast?.success('Added to favorites');
        }
      } else {
        const { error: removeError } = await userService?.removeFavoriteSong(user?.id, songId);
        if (removeError) {
          setIsFavorite(!newValue);
          toast?.error('Failed to remove from favorites');
        } else {
          toast?.success('Removed from favorites');
        }
      }
    } catch {
      setIsFavorite(!newValue);
      toast?.error('Failed to update favorites');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleEdit = () => {
    if (!isSuperUser()) {
      setError('You do not have permission to edit songs');
      return;
    }
    
    // Show character selection modal in all cases to let user choose which character to edit
    // The modal will handle the case where no characters are assigned
    setShowCharacterModal(true);
  };

  const handleCharacterSelect = (character) => {
    setSelectedCharacter(character);
    setShowCharacterModal(false);
    setIsEditing(true);
  };

  const handleCreateCharacterRedirect = () => {
    setShowCharacterModal(false);
    // Navigate to roles page to assign characters
    navigate('/all-roles');
  };

  const handleSaveEdit = async (updatedSongData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate required fields before sending
      if (!updatedSongData?.title?.trim()) {
        setError('Song title is required');
        return;
      }

      if (!updatedSongData?.musical_id) {
        setError('Musical selection is required');
        return;
      }

      // If we have a selected character, handle character-specific analysis
      if (selectedCharacter) {
        const analysisData = {
          overview: updatedSongData?.overview || {},
          level1: updatedSongData?.level1 || '',
          level2: updatedSongData?.level2 || '',
          level3: updatedSongData?.level3 || [],
          level4: updatedSongData?.level4 || {},
          level5: updatedSongData?.level5 || {},
          phrases: updatedSongData?.phrases || [],
          video_url_overview: updatedSongData?.video_url_overview ?? null,
          video_url_level1: updatedSongData?.video_url_level1 ?? null,
          video_url_level2: updatedSongData?.video_url_level2 ?? null,
          video_url_level3: updatedSongData?.video_url_level3 ?? null,
          video_url_level4: updatedSongData?.video_url_level4 ?? null,
          video_url_level5: updatedSongData?.video_url_level5 ?? null,
          updated_by: user?.id
        };

        let result;
        
        // Check if we need to create new analysis or update existing
        if (selectedCharacter?.id) {
          // Update existing character analysis
          result = await characterSongAnalysisService?.updateCharacterAnalysis(selectedCharacter?.id, analysisData);
        } else if (selectedCharacter?.role_id) {
          // Create new character analysis
          result = await characterSongAnalysisService?.createCharacterAnalysis({
            song_id: songId,
            role_id: selectedCharacter?.role_id,
            ...analysisData,
            created_by: user?.id
          });
        } else {
          setError('Invalid character selection');
          return;
        }

        if (result?.error) {
          console.error('Character analysis error:', result?.error);
          setError(`Failed to save character analysis: ${result?.error}`);
          return;
        }

        console.log('Character analysis saved successfully:', result?.data);
      }

      // Also update basic song information
      const basicSongData = {
        title: updatedSongData?.title?.trim(),
        musical_id: updatedSongData?.musical_id,
        act_number: updatedSongData?.act_number || null,
        song_order: updatedSongData?.song_order || null,
        key_signature: updatedSongData?.key_signature || null,
        is_featured: updatedSongData?.is_featured || false,
        updated_by: user?.id
      };

      const { data: updatedSong, error: updateError } = await updateSong(songId, basicSongData);

      if (updateError) {
        console.error('Update song error:', updateError);
        setError(`Failed to save song changes: ${updateError}`);
        return;
      }

      if (!updatedSong) {
        setError('Failed to save changes: No data returned from update');
        return;
      }

      console.log('Song updated successfully:', updatedSong);
      
      // Exit edit mode and reload data to refresh the view
      setIsEditing(false);
      setSelectedCharacter(null);
      
      // Refresh all data to show updated content
      await Promise.all([
        loadSongData(),
        loadCharacterAnalyses()
      ]);
      
    } catch (err) {
      console.error('Error in handleSaveEdit:', err);
      setError(`Failed to save changes: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedCharacter(null);
  };

  const handleBack = () => {
    navigate('/song-database-list');
  };

  const handleSongUpdate = async () => {
    // Reload song data when files are uploaded
    await loadSongData();
  };

  const loadMusicals = async () => {
    try {
      const { data: musicalsData, error: musicalsError } = await musicalService?.getMusicals();
      if (!musicalsError) {
        setMusicals(musicalsData || []);
      }
    } catch (err) {
      console.error('Error loading musicals:', err);
      // Don't set error for musicals loading failure
    }
  };

  const loadCharacterAnalyses = async () => {
    if (!songId) return;

    try {
      // Use the new getAllSongCharacters method that includes both existing analyses 
      // AND characters assigned via song_roles that don't have analyses yet
      const { data, error: analysisError } = await characterSongAnalysisService?.getAllSongCharacters(songId);
      if (!analysisError && data) {
        setCharacterAnalyses(data);
      }
    } catch (err) {
      console.error('Error loading character analyses:', err);
      // Don't set error state for character analyses failure - song can still be shown
    }
  };

  const loadSongData = async () => {
    if (!songId) {
      setError('No song ID provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load song data
      const { data: songData, error: songError } = await getSongById(songId);
      
      if (songError) {
        console.error('Load song error:', songError);
        setError(`Failed to load song: ${songError}`);
        return;
      }

      if (!songData) {
        setError('Song not found');
        return;
      }

      setSong(songData);

      // Load analysis data if available
      try {
        const { data: analysisData } = await SongAnalysisService?.getSongAnalysisById(songId);
        if (analysisData) {
          setAnalysis(analysisData);
        }
      } catch (analysisError) {
        console.error('Failed to load analysis:', analysisError);
        // Don't set error state for analysis failure - song details can still be shown
      }

    } catch (err) {
      console.error('Error loading song data:', err);
      setError(`Failed to load song details: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCharacterChange = (characterData) => {
    setSelectedCharacter(characterData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="flex items-center justify-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Helmet>
          <title>Error Loading Song - RoleCall</title>
        </Helmet>
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error Loading Song
              </h3>
              <p className="text-gray-600 mb-6">
                {error}
              </p>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back to Song List
                </Button>
                <Button
                  variant="default"
                  onClick={() => window.location?.reload()}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Helmet>
        <title>{song?.title ? `${song?.title} - Song Details` : 'Song Details'} - RoleCall</title>
        <meta name="description" content={`Detailed analysis of ${song?.title || 'song'} from ${song?.musical?.title || 'musical'}`} />
      </Helmet>
      <Header />
      <main className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={handleBack}
              iconName="ArrowLeft"
              iconPosition="left"
              className="border-white text-white hover:bg-white hover:text-indigo-900"
            >
              Back to Songs
            </Button>
            <div>
              <h1 className="text-3xl font-heading font-bold text-white">
                {song?.title || 'Song Details'}
              </h1>
              {song?.musical?.title && (
                <p className="text-blue-100 font-body mt-1">
                  from{' '}
                  <Link
                    to={`/add-musical?id=${song?.musical?.id}`}
                    className="hover:text-white hover:underline transition-colors cursor-pointer"
                  >
                    {song?.musical?.title}
                  </Link>
                  {song?.musical?.year_premiered && ` (${song?.musical?.year_premiered})`}
                </p>
              )}
              {selectedCharacter && isEditing && (
                <p className="text-yellow-200 font-body mt-1">
                  Editing for: {selectedCharacter?.role?.character_name}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            {/* Heart - Favorite Song (all logged-in users) */}
            {user && !isEditing && (
              <button
                onClick={handleToggleFavorite}
                disabled={favoriteLoading}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 disabled:opacity-50 ${
                  isFavorite
                    ? 'bg-red-500 border-red-500 text-white shadow-lg shadow-red-500/30'
                    : 'border-red-400 text-red-400 hover:bg-red-500 hover:border-red-500 hover:text-white'
                }`}
              >
                <Icon name="Heart" size={18} className={isFavorite ? 'fill-current' : ''} />
              </button>
            )}
            {isSuperUser() && (
              <>
                {!isEditing && (
                  <>
                    {/* Star - Feature Song (superuser only) */}
                    <button
                      onClick={handleToggleFeatured}
                      disabled={featuredLoading}
                      title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 disabled:opacity-50 ${
                        isFeatured
                          ? 'bg-yellow-400 border-yellow-400 text-white shadow-lg shadow-yellow-400/30'
                          : 'border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-white'
                      }`}
                    >
                      <Icon name="Star" size={18} className={isFeatured ? 'fill-current' : ''} />
                    </button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAiPanel(prev => !prev)}
                      iconName="Sparkles"
                      iconPosition="left"
                      className="border-purple-300 text-purple-200 hover:bg-purple-600 hover:border-purple-600 hover:text-white"
                    >
                      Generate with AI
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleEdit}
                      iconName="Edit"
                      iconPosition="left"
                      className="border-white text-white hover:bg-white hover:text-indigo-900"
                    >
                      Edit Song
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* AI Generation Panel — admin only */}
        {isSuperUser() && showAiPanel && !isEditing && (
          <div className="mb-8 bg-white bg-opacity-10 backdrop-blur-sm border border-purple-400 border-opacity-40 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-purple-500 bg-opacity-30 flex items-center justify-center">
                  <Icon name="Sparkles" size={16} className="text-purple-200" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">AI Song Analysis</h3>
                  <p className="text-blue-200 text-sm">
                    Auto-generate context, emotional arc, and performance tips
                    {(selectedCharacter?.role?.character_name || characterAnalyses?.[0]?.role?.character_name) && (
                      <span className="ml-1">
                        for <span className="text-purple-300 font-medium">{selectedCharacter?.role?.character_name || characterAnalyses?.[0]?.role?.character_name}</span>
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleGenerateAi}
                disabled={isAiLoading}
                iconName={isAiLoading ? 'Loader' : 'Zap'}
                iconPosition="left"
                className="border-purple-400 text-purple-200 hover:bg-purple-600 hover:border-purple-600 hover:text-white disabled:opacity-50"
              >
                {isAiLoading ? 'Generating...' : aiGenerationDone ? 'Regenerate' : 'Generate'}
              </Button>
            </div>

            {isAiLoading && (
              <div className="flex items-center justify-center py-10">
                <div className="flex items-center space-x-3 text-purple-200">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm">Generating AI analysis for "{song?.title}"...</span>
                </div>
              </div>
            )}

            {!isAiLoading && aiGenerationDone && (
              <div className="space-y-5">
                {/* Song Context */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-purple-200 text-sm font-semibold uppercase tracking-wide">
                      Song Context
                    </label>
                    <button
                      onClick={() => handleCopyField('context', aiSongContext)}
                      className="flex items-center space-x-1 text-xs text-blue-300 hover:text-white transition-colors"
                    >
                      <Icon name={copiedField === 'context' ? 'Check' : 'Copy'} size={13} />
                      <span>{copiedField === 'context' ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                  <textarea
                    value={aiSongContext}
                    onChange={(e) => setAiSongContext(e?.target?.value)}
                    rows={3}
                    className="w-full bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-blue-300 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    placeholder="Song context will appear here..."
                  />
                </div>

                {/* Emotional Arc */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-purple-200 text-sm font-semibold uppercase tracking-wide">
                      Emotional Arc
                    </label>
                    <button
                      onClick={() => handleCopyField('arc', aiEmotionalArc)}
                      className="flex items-center space-x-1 text-xs text-blue-300 hover:text-white transition-colors"
                    >
                      <Icon name={copiedField === 'arc' ? 'Check' : 'Copy'} size={13} />
                      <span>{copiedField === 'arc' ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                  <textarea
                    value={aiEmotionalArc}
                    onChange={(e) => setAiEmotionalArc(e?.target?.value)}
                    rows={3}
                    className="w-full bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-blue-300 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    placeholder="Emotional arc will appear here..."
                  />
                </div>

                {/* Performance Tips */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-purple-200 text-sm font-semibold uppercase tracking-wide">
                      Performance Tips
                    </label>
                    <button
                      onClick={() => handleCopyField('tips', aiPerformanceTips)}
                      className="flex items-center space-x-1 text-xs text-blue-300 hover:text-white transition-colors"
                    >
                      <Icon name={copiedField === 'tips' ? 'Check' : 'Copy'} size={13} />
                      <span>{copiedField === 'tips' ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                  <textarea
                    value={aiPerformanceTips}
                    onChange={(e) => setAiPerformanceTips(e?.target?.value)}
                    rows={4}
                    className="w-full bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-blue-300 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    placeholder="Performance tips will appear here..."
                  />
                </div>

                {/* Copy All */}
                <div className="flex justify-end pt-1">
                  <button
                    onClick={() => handleCopyField('all', `Song Context:\n${aiSongContext}\n\nEmotional Arc:\n${aiEmotionalArc}\n\nPerformance Tips:\n${aiPerformanceTips}`)}
                    className="flex items-center space-x-2 text-sm text-purple-300 hover:text-white border border-purple-400 border-opacity-50 hover:border-purple-400 rounded-lg px-4 py-2 transition-colors"
                  >
                    <Icon name={copiedField === 'all' ? 'Check' : 'ClipboardCopy'} size={14} />
                    <span>{copiedField === 'all' ? 'All Copied!' : 'Copy All'}</span>
                  </button>
                </div>
              </div>
            )}

            {!isAiLoading && !aiGenerationDone && (
              <div className="text-center py-8 text-blue-300 text-sm">
                <Icon name="Sparkles" size={28} className="mx-auto mb-3 opacity-40" />
                <p>Click <span className="text-purple-300 font-medium">Generate</span> to create AI-powered analysis for this song.</p>
              </div>
            )}
          </div>
        )}

        {/* Character Selection Modal */}
        {showCharacterModal && (
          <CharacterSelectionModal
            characters={characterAnalyses}
            onSelect={handleCharacterSelect}
            onClose={() => setShowCharacterModal(false)}
            onCreateCharacter={handleCreateCharacterRedirect}
          />
        )}

        {/* Content */}
        {!loading && !error && song && (
          <>
            {isEditing ? (
              <SongDetailEdit
                song={song}
                musicals={musicals}
                selectedCharacter={selectedCharacter}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
                onError={setError}
              />
            ) : (
              <SongDetailView
                song={song}
                characterAnalyses={characterAnalyses}
                selectedCharacter={selectedCharacter}
                onCharacterChange={handleCharacterChange}
                onSongUpdate={handleSongUpdate}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default SongDetailPage;